import React, { useState } from 'react';
import { TAB_DEFAULT_DIRECTION } from '../engine.js';
import {
  formatBookScopePostureLabel,
  formatBookScopePostureTooltip,
  formatLedgerBookSettingsSummary,
  formatLedgerBookSettingsTooltip,
  formatSourceStateLabel,
  formatSourceStateTooltip,
  hasBookScopePosture,
  hasLedgerBookSettings,
  normalizeSourceState,
} from '../source-state.js';
import { PeriodPicker, DensityPicker, DirectionPicker, VerbosityPicker } from './FormalStatementSection.jsx';
import ScenarioStatus from './ScenarioStatus.jsx';

const CHIP = 'flex flex-col px-[0.7rem] border-r border-border leading-tight [&:last-of-type]:border-r-0';
const LABEL = 'text-[0.65rem] text-text-dim uppercase tracking-[0.06em]';

export default function ScenarioBar({ scenario, sourceState, setScenario, onJumpToSettings, activeTab }) {
  const [collapsed, setCollapsed] = useState(true);
  const normalizedSource = normalizeSourceState(sourceState);
  const showBookSettings = hasLedgerBookSettings(normalizedSource);
  const showBookScope = hasBookScopePosture(normalizedSource);
  const includeAdj  = scenario.includeAdjustments !== false;
  const adjCount    = (scenario.adjustingEntries ?? []).filter(e => e.status === 'posted').length;
  const toggleAdj   = () => setScenario && setScenario({ ...scenario, includeAdjustments: !includeAdj });
  const sparkGrad   = !!scenario.sparklineGradient;
  const toggleSpark = () => setScenario && setScenario({ ...scenario, sparklineGradient: !sparkGrad });
  /* v8 Path U — trend-column + in-cell row-sparkline toggles. Default both
     to ON so shipped experience shows the full DRY treatment; users opt
     out if they want a clean filing-style look. Persisted in model state so
     Model Profile saves remember the preference. */
  const showTrend   = scenario.showTrendColumn !== false;
  const toggleTrend = () => setScenario && setScenario({ ...scenario, showTrendColumn: !showTrend });
  const showRowSpark   = scenario.showRowSparkline !== false;
  const toggleRowSpark = () => setScenario && setScenario({ ...scenario, showRowSparkline: !showRowSpark });
  /* v8 Path AI — semantic tone toggle. When ON, sparkline / row-segment
     / delta-badge colors reflect metric POLARITY (betterWhen: higher|
     lower|neutral) instead of raw direction. Rising Debt-to-Equity
     shows red (unfavorable), rising Savings rate shows green (favorable).
     Default OFF so the existing "rising = green" experience is preserved
     until the user opts in. */
  const semanticTone    = !!scenario.sparklineSemanticTone;
  const toggleSemantic  = () => setScenario && setScenario({ ...scenario, sparklineSemanticTone: !semanticTone });

  const barPad = collapsed ? 'py-[0.4rem] px-6' : 'py-[0.55rem] px-6';
  const collapsedSourceTooltip = [
    formatSourceStateTooltip(normalizedSource),
    showBookSettings ? formatLedgerBookSettingsTooltip(normalizedSource) : null,
    showBookScope ? formatBookScopePostureTooltip(normalizedSource) : null,
  ].filter(Boolean).join(' ');

  return (
    <div className={`flex items-center gap-[0.6rem] ${barPad} bg-surface border-b border-border text-[0.8rem] flex-wrap shrink-0 z-[24]`}>
      <button
        type="button"
        className="bg-transparent border border-border text-text-muted cursor-pointer px-[6px] py-[2px] rounded-[4px] text-[0.7rem] hover:text-text hover:border-border-strong"
        onClick={() => setCollapsed(c => !c)}
        title={collapsed ? 'Expand model context' : 'Collapse model context'}
        aria-label={collapsed ? 'Expand model context' : 'Collapse model context'}
      >
        {collapsed ? '▸' : '▾'}
      </button>

      {!collapsed && (
        <>
          <ScenarioStatus scenario={scenario} sourceState={normalizedSource} onJumpToSettings={onJumpToSettings} />
          <div className={CHIP} title={formatSourceStateTooltip(normalizedSource)}>
            <span className={LABEL}>Source state</span>
            <strong className="text-[0.82rem] text-text">{formatSourceStateLabel(normalizedSource, { compact: true })}</strong>
          </div>
          {showBookSettings && (
            <div className={CHIP} title={formatLedgerBookSettingsTooltip(normalizedSource)}>
              <span className={LABEL}>Book settings</span>
              <strong className="text-[0.82rem] text-text">{formatLedgerBookSettingsSummary(normalizedSource, { compact: true })}</strong>
            </div>
          )}
          {showBookScope && (
            <div className={CHIP} title={formatBookScopePostureTooltip(normalizedSource)}>
              <span className={LABEL}>Book scope</span>
              <strong className="text-[0.82rem] text-text">{formatBookScopePostureLabel(normalizedSource, { compact: true })}</strong>
            </div>
          )}
          <button
            type="button"
            className={`cursor-pointer bg-transparent border border-border-strong rounded-sm text-text font-[inherit] inline-flex flex-col items-start px-[0.55rem] py-[0.15rem] leading-[1.1] transition-colors hover:border-accent-gold ${includeAdj ? '' : 'opacity-85'}`}
            onClick={toggleAdj}
            title={`${includeAdj ? 'Excluding' : 'Including'} adjusting entries (${adjCount} posted). Click to toggle.`}
            aria-pressed={includeAdj}
          >
            <span className={LABEL}>Include JEs</span>
            <strong className={`text-[0.82rem] ${includeAdj ? 'text-accent-green' : 'text-text-dim'}`}>{includeAdj ? 'ON' : 'OFF'}{adjCount > 0 ? ` · ${adjCount}` : ''}</strong>
          </button>
          <button
            type="button"
            className={`cursor-pointer bg-transparent border border-border-strong rounded-sm text-text font-[inherit] inline-flex flex-col items-start px-[0.55rem] py-[0.15rem] leading-[1.1] transition-colors hover:border-accent-gold ${showTrend ? '' : 'opacity-85'}`}
            onClick={toggleTrend}
            title={`Trend column (MiniSparkline + delta) ${showTrend ? 'VISIBLE' : 'HIDDEN'}. Applies to every multi-period table.`}
            aria-pressed={showTrend}
          >
            <span className={LABEL}>Trend col</span>
            <strong className={`text-[0.82rem] ${showTrend ? 'text-accent-green' : 'text-text-dim'}`}>{showTrend ? 'ON' : 'OFF'}</strong>
          </button>
          <button
            type="button"
            className={`cursor-pointer bg-transparent border border-border-strong rounded-sm text-text font-[inherit] inline-flex flex-col items-start px-[0.55rem] py-[0.15rem] leading-[1.1] transition-colors hover:border-accent-gold ${showRowSpark ? '' : 'opacity-85'}`}
            onClick={toggleRowSpark}
            title={`In-cell row sparkline ${showRowSpark ? 'VISIBLE' : 'HIDDEN'}. Applies to every multi-period table (behind the numbers).`}
            aria-pressed={showRowSpark}
          >
            <span className={LABEL}>Row spark</span>
            <strong className={`text-[0.82rem] ${showRowSpark ? 'text-accent-green' : 'text-text-dim'}`}>{showRowSpark ? 'ON' : 'OFF'}</strong>
          </button>
          <button
            type="button"
            className={`cursor-pointer bg-transparent border border-border-strong rounded-sm text-text font-[inherit] inline-flex flex-col items-start px-[0.55rem] py-[0.15rem] leading-[1.1] transition-colors hover:border-accent-gold ${sparkGrad ? '' : 'opacity-85'}`}
            onClick={toggleSpark}
            title={`Area gradient under row sparklines is ${sparkGrad ? 'ON' : 'OFF'}. Click to toggle.`}
            aria-pressed={sparkGrad}
          >
            <span className={LABEL}>Spark fill</span>
            <strong className={`text-[0.82rem] ${sparkGrad ? 'text-accent-green' : 'text-text-dim'}`}>{sparkGrad ? 'ON' : 'OFF'}</strong>
          </button>
          <button
            type="button"
            className={`cursor-pointer bg-transparent border border-border-strong rounded-sm text-text font-[inherit] inline-flex flex-col items-start px-[0.55rem] py-[0.15rem] leading-[1.1] transition-colors hover:border-accent-gold ${semanticTone ? '' : 'opacity-85'}`}
            onClick={toggleSemantic}
            title={`Semantic tone (favorable/unfavorable per metric polarity) is ${semanticTone ? 'ON' : 'OFF'}. When ON: rising debt shows red, rising savings shows green. When OFF: rising = green always.`}
            aria-pressed={semanticTone}
          >
            <span className={LABEL}>Semantic tone</span>
            <strong className={`text-[0.82rem] ${semanticTone ? 'text-accent-green' : 'text-text-dim'}`}>{semanticTone ? 'ON' : 'OFF'}</strong>
          </button>

          {/* v8 — global period + density pickers. Apply app-wide to every
              multi-period statement table. Replaced the duplicated per-tab
              pickers inside each FormalStatementSection. */}
          <div className="flex flex-col leading-tight gap-[2px] px-[0.7rem] border-l border-border ml-1">
            <span className={LABEL}>Periods × density</span>
            <div className="flex items-center gap-2 flex-wrap">
              <PeriodPicker scenario={scenario} setScenario={setScenario} />
              <DensityPicker scenario={scenario} setScenario={setScenario} />
            </div>
          </div>

          {/* v8 Path C — per-tab window direction. Hidden on tabs without
              a direction default (e.g., Settings). */}
          {TAB_DEFAULT_DIRECTION[activeTab] && (
            <div className="flex flex-col leading-tight gap-[2px] px-[0.7rem] border-l border-border">
              <span className={LABEL}>Direction</span>
              <DirectionPicker scenario={scenario} setScenario={setScenario} activeTab={activeTab} />
            </div>
          )}

          {/* v8 Path L — universal verbosity toggle. Drives how much
              richness (sparklines, secondary sections, rich tooltips)
              is shown by default. One control, app-wide effect. */}
          <div className="flex flex-col leading-tight gap-[2px] px-[0.7rem] border-l border-border">
            <span className={LABEL}>Verbosity</span>
            <VerbosityPicker scenario={scenario} setScenario={setScenario} />
          </div>

          <button
            type="button"
            className="ml-auto bg-transparent border border-accent-gold text-accent-gold px-[0.7rem] py-[0.3rem] rounded-sm cursor-pointer text-[0.75rem] font-[inherit] hover:bg-[rgba(240,180,41,0.10)]"
            onClick={onJumpToSettings}
            title="Open Settings -> full model assumptions panel"
          >
            Edit →
          </button>
        </>
      )}

      {collapsed && (
        <span className="text-text-muted text-[0.78rem]" title={collapsedSourceTooltip}>
          {scenario.activeProfile ? (scenario.profiles?.[scenario.activeProfile]?.name || 'Profile') : 'Default · Base'}
          {' · '}{formatSourceStateLabel(normalizedSource, { compact: true })}
          {showBookSettings ? ` · ${formatLedgerBookSettingsSummary(normalizedSource, { compact: true })}` : ''}
          {showBookScope ? ` · ${formatBookScopePostureLabel(normalizedSource, { compact: true })}` : ''}
        </span>
      )}
    </div>
  );
}
